import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import { formateDateWithMonth, handleServerValidations, globalLoader } from "../../../../utils/commonfunction";
import Loader from "../../common/loader";
import Breadcrums from "../../common/breadcrumbs";
import CustomPagination from "../../common/custompagination";
import { List, Status, UploadReport } from "../../services/questionBooking.services";

const QuestionBookingList = () => {
    const { t } = useTranslation();
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    
    const [list, setList] = useState([]);
    const [datalength, setDataLength] = useState(0);
    const [itemperpage] = useState(10);
    const [page, setPage] = useState(1);
    const [loader, setLoader] = useState(true);
    const [globalsearch, setGlobalSearch] = useState("");
    const [statsupdate, setStatusUpdate] = useState(false);
    const fileInputRef = useRef(null);
    const [selectedBookingId, setSelectedBookingId] = useState(null);

    const breadcrumbs = [
        { title: t("link_dashboard"), url: "/admin/dashboard" },
        { title: "Reports", url: "/admin/report/list/1" },
        { title: "Question Bookings", url: "" },
    ];

    useEffect(() => {
        const getData = setTimeout(() => {
            setLoader(true);
            const queryParams = {
                page: params.pgno || 1,
                per_page: itemperpage,
                search: globalsearch
            };

            List(queryParams)
                .then((response) => {
                    if (response && response.data) {
                        setDataLength(response.data.total_records || 0);
                        setList(response.data.list || []);
                        setPage(response.data.page || 1);
                    }
                    setLoader(false);
                })
                .catch((error) => {
                    console.log("error ====> ", error);
                    setLoader(false);
                });
        }, 300);
        return () => clearTimeout(getData);
    }, [location, globalsearch, itemperpage, params.pgno, statsupdate]);

    const handleUploadClick = (id) => {
        setSelectedBookingId(id);
        fileInputRef.current.click();
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file && selectedBookingId) {
            const formData = new FormData();
            formData.append("id", selectedBookingId);
            formData.append("report_file", file);
            
            globalLoader(true);
            UploadReport(formData)
                .then(res => {
                    if (res.status || res.success) {
                        Swal.fire({
                            icon: "success",
                            text: res.message,
                            ...SWAL_SETTINGS,
                        });
                        setStatusUpdate(!statsupdate);
                    } else {
                        Swal.fire({
                            icon: "error",
                            text: handleServerValidations(res),
                            ...SWAL_SETTINGS,
                        });
                    }
                    globalLoader(false);
                    e.target.value = null; // Reset file input
                })
                .catch(err => {
                    console.error(err);
                    globalLoader(false);
                    e.target.value = null;
                });
        }
    };

    const handleStatusChange = (id, newStatus) => {
        Swal.fire({
            customClass: "swal-wide",
            title: t("msg_are_you_sure"),
            text: `Change booking status to "${newStatus}"?`,
            icon: "warning",
            showCancelButton: true,
            cancelButtonText: t("btn_cancel"),
            confirmButtonColor: "#403fad",
            cancelButtonColor: "#f1388b",
            confirmButtonText: t("btn_yes"),
        }).then((result) => {
            if (result.isConfirmed) {
                globalLoader(true);
                Status(id, newStatus)
                    .then((res) => {
                        if (res.status || res.success) {
                            Swal.fire({
                                icon: "success",
                                text: res.message || "Status updated successfully!",
                                ...SWAL_SETTINGS,
                            });
                            setStatusUpdate(!statsupdate);
                        } else {
                            Swal.fire({
                                icon: "error",
                                text: handleServerValidations(res),
                                ...SWAL_SETTINGS,
                            });
                        }
                        globalLoader(false);
                    })
                    .catch((err) => {
                        console.error(err);
                        Swal.fire({
                            icon: "error",
                            text: "Something went wrong. Please try again.",
                            ...SWAL_SETTINGS,
                        });
                        globalLoader(false);
                    });
            }
        });
    };

    return (
        <>
            <Breadcrums data={breadcrumbs} />
            <div className="animation_fade">
                <div className="card custom-card">
                    <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h6 className="main-content-label">Question Bookings</h6>
                            <div className="d-flex align-items-center">
                                <div className="form-group mb-0">
                                    <div className="form-group mb-0 rangepicker_container filter_design">
                                        <i className="fa fa-search calender_icon"></i>
                                        <input
                                            type="search"
                                            className="form-control"
                                            value={globalsearch}
                                            placeholder={t("input_placeholder_search")}
                                            onChange={(e) => setGlobalSearch(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <input 
                            type="file" 
                            ref={fileInputRef} 
                            style={{ display: "none" }} 
                            onChange={handleFileChange}
                            accept=".pdf,.doc,.docx"
                        />

                        <div className="table-responsive">
                            <table className="table table-bordered border-t0 key-buttons text-nowrap w-100">
                                <thead>
                                    <tr>
                                        <th>{t("list_heading_sno")}</th>
                                        <th>Order Number</th>
                                        <th>User Details</th>
                                        <th>Question</th>
                                        <th>Amount</th>
                                        <th>Booking Date</th>
                                        <th>Status</th>
                                        <th>Report File</th>
                                        <th className="action_head">{t("list_heading_action")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loader ? (
                                        <tr>
                                            <td colSpan={9}>
                                                <Loader />
                                            </td>
                                        </tr>
                                    ) : (
                                        <>
                                            {list.length ? (
                                                list.map((row, i) => (
                                                    <tr key={row.id}>
                                                        <td>{(page - 1) * itemperpage + i + 1}</td>
                                                        <td className="fw-bold text-primary">{row.order_number || `ORD-QB-${row.id}`}</td>
                                                        <td>
                                                            {row?.user?.name || "N/A"}
                                                            <br/>
                                                            <small className="text-muted">{row?.user?.email || ""}</small>
                                                        </td>
                                                        <td style={{ maxWidth: "200px" }}>
                                                            {(() => {
                                                                let qData = row?.report_question?.question || row?.question;
                                                                if (!qData) return "N/A";
                                                                let qArray = [];
                                                                if (typeof qData === 'string') {
                                                                    try {
                                                                        qArray = JSON.parse(qData);
                                                                        if (!Array.isArray(qArray)) qArray = [qArray];
                                                                    } catch (e) {
                                                                        let cleanStr = qData.trim();
                                                                        if (cleanStr.startsWith('[') && cleanStr.endsWith(']')) {
                                                                            cleanStr = cleanStr.substring(1, cleanStr.length - 1);
                                                                            qArray = cleanStr.split(',').map(s => s.trim()).filter(s => s);
                                                                        } else {
                                                                            qArray = [qData];
                                                                        }
                                                                    }
                                                                } else if (Array.isArray(qData)) {
                                                                    qArray = qData;
                                                                } else {
                                                                    qArray = [qData];
                                                                }
                                                                
                                                                if (qArray.length > 1) {
                                                                    return (
                                                                        <div>
                                                                            <span className="badge bg-primary mb-1">{qArray.length} Questions</span>
                                                                            <div className="text-muted text-truncate" title={qArray[0]} style={{ maxWidth: "200px", fontSize: "12px" }}>
                                                                                1. {qArray[0]}...
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                } else {
                                                                    return (
                                                                        <div className="text-truncate" title={qArray[0]} style={{ maxWidth: "200px" }}>
                                                                            • {qArray[0]}
                                                                        </div>
                                                                    );
                                                                }
                                                            })()}
                                                        </td>
                                                        <td>{row?.currency_type} {row?.amount}</td>
                                                        <td>{formateDateWithMonth(row?.created_at)}</td>
                                                        <td>
                                                            <div className="d-flex align-items-center gap-2">
                                                                <span className={`badge ${
                                                                    row.status === 'answered' ? 'bg-success' : 
                                                                    row.status === 'cancelled' ? 'bg-danger' : 
                                                                    row.status === 'failed' ? 'bg-danger' : 
                                                                    'bg-warning'
                                                                }`}>
                                                                    {row.status}
                                                                </span>
                                                                <select
                                                                    className="form-select form-select-sm w-auto"
                                                                    value={row.status}
                                                                    onChange={(e) => handleStatusChange(row.id, e.target.value)}
                                                                    style={{ padding: '0.25rem 1.75rem 0.25rem 0.5rem', fontSize: '0.75rem' }}
                                                                >
                                                                    <option value="pending">Pending</option>
                                                                    <option value="answered">Answered</option>
                                                                    <option value="cancelled">Cancelled</option>
                                                                    <option value="failed">Failed</option>
                                                                </select>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            {row.file ? (
                                                                <a href={row.file} target="_blank" rel="noreferrer" className="btn btn-sm btn-info">
                                                                    <i className="fa fa-download me-1"></i> View Report
                                                                </a>
                                                            ) : (
                                                                <span className="text-muted">Not Uploaded</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <div className="d-flex gap-2">
                                                                <button
                                                                    className="btn btn-sm btn-info"
                                                                    onClick={() => navigate(`/admin/report/question-bookings/view/${row.id}`)}
                                                                >
                                                                    <i className="fa fa-eye me-1"></i> View
                                                                </button>
                                                                <button
                                                                    className="btn btn-sm ripple btn-main-primary signbtn"
                                                                    onClick={() => handleUploadClick(row.id)}
                                                                >
                                                                    <i className="fa fa-upload me-1"></i> 
                                                                    {row.file ? "Re-upload" : "Upload Report"}
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={9} className="text-center">
                                                        {t("msg_no_records")}
                                                    </td>
                                                </tr>
                                            )}
                                        </>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="mt-3">
                            <b>{t("msg_total_records")} : {datalength ? datalength : "0"}</b>
                        </div>
                        {datalength && datalength > 0 ? (
                            <CustomPagination
                                datalength={datalength}
                                itemperpage={itemperpage}
                                currentPage={page}
                                setPage={setPage}
                                pageRoute={[
                                    {
                                        name: "Question Bookings",
                                        path: "/admin/report/question-bookings/list",
                                    },
                                ]}
                            />
                        ) : (
                            ""
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default QuestionBookingList;
